/**
 * Unpublished shared-directory drafts and the dialog that publishes them as a
 * release of core's shared directory. Shown on the Shared Doctors page and on
 * the Physicians page of a clinic that routes from the directory.
 */

import { useEffect, useMemo, useState } from 'react'
import { Card, Button, Badge, Input, Label, Textarea, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui'
import { sharedPhysiciansAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { useSharedDraftStore, SHARED_DIRECTORY_SCOPE } from '../stores/draftStore'

const errorMessage = (err) => err?.response?.data?.error?.message || err?.message || 'Something went wrong'

/** The record as core should store it: no id (immutable, in the URL) and none of this page's `_draft` markers. */
function forCore(doc) {
  if (!doc) return doc
  return Object.fromEntries(Object.entries(doc).filter(([k]) => k !== 'id' && k !== '_id' && !k.startsWith('_')))
}

/** One change per record: the last update wins, a delete overrides updates. */
export function collapse(changes) {
  const out = new Map()
  for (const c of changes) {
    const key = c.operation === 'create' ? `create:${c.id}` : c.resource_id
    if (out.get(key)?.operation === 'delete') continue
    out.set(key, c)
  }
  return [...out.values()].map(c => ({ resource_type: 'physician', resource_id: c.operation === 'create' ? null : c.resource_id, operation: c.operation, data: forCore(c.new_data) }))
}

function PublishDialog({ open, onClose, changes, onPublished }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const collapsed = useMemo(() => collapse(changes), [changes])
  useEffect(() => { if (open) { setError(null); setBusy(false) } }, [open])

  const publish = async () => {
    setBusy(true); setError(null)
    try {
      await sharedPhysiciansAPI.publish({ release_name: name.trim(), description: description.trim(), changes: collapsed })
      setName(''); setDescription('')
      onPublished()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => !o && !busy && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Publish to the shared directory</DialogTitle>
          <DialogDescription>
            {collapsed.length} change{collapsed.length === 1 ? '' : 's'}. Applied immediately for every clinic routing from the directory.
          </DialogDescription>
        </DialogHeader>
        <ul className="max-h-48 space-y-1 overflow-y-auto rounded border p-2 text-sm">
          {changes.map(c => <li key={c.id}><Badge variant="outline" className="mr-2">{c.operation}</Badge>{c.description || c.resource_id}</li>)}
        </ul>
        <div className="space-y-2">
          <Label htmlFor="rel-name">Release name *</Label>
          <Input id="rel-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. October license renewals" />
          <Label htmlFor="rel-desc">Description</Label>
          <Textarea id="rel-desc" value={description} onChange={e => setDescription(e.target.value)} rows={3} />
        </div>
        {error && <p className="rounded bg-red-50 p-2 text-sm text-red-700">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={publish} disabled={busy || !name.trim() || collapsed.length === 0}>{busy ? 'Publishing…' : 'Publish'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}


export default function SharedDraftBar({ onPublished }) {
  const { user } = useAuthStore()
  const { changes, initialize, removeChange, clearDrafts } = useSharedDraftStore()
  const [open, setOpen] = useState(false)
  useEffect(() => { if (user?.id) initialize(SHARED_DIRECTORY_SCOPE, user.id) }, [user?.id, initialize])
  if (!changes.length) return null
  return (
    <>
      <Card className="flex flex-wrap items-center gap-3 border-amber-300 bg-amber-50 p-3">
        <span className="text-sm font-medium">{changes.length} unpublished shared-directory change{changes.length === 1 ? '' : 's'}</span>
        <div className="flex flex-1 flex-wrap gap-1">
          {changes.slice(-4).map(c => (
            <Badge key={c.id} variant="outline" className="cursor-pointer" title="Discard this draft" onClick={() => removeChange(c.id)}>
              {c.description || `${c.operation} ${c.resource_id}`} ✕
            </Badge>
          ))}
          {changes.length > 4 && <span className="text-xs text-muted-foreground">+{changes.length - 4} more</span>}
        </div>
        <Button size="sm" variant="outline" onClick={() => confirm('Discard every unpublished change?') && clearDrafts()}>Discard all</Button>
        <Button size="sm" onClick={() => setOpen(true)}>Review & publish</Button>
      </Card>
      <PublishDialog
        open={open}
        onClose={() => setOpen(false)}
        changes={changes}
        onPublished={() => { clearDrafts(); setOpen(false); onPublished?.() }}
      />
    </>
  )
}
